import { useMemo, useState, type ReactNode } from "react";
import ModalDialog, {
  ModalDialogBody,
  ModalDialogFooter,
} from "../UI/ModalDialog";
import Button from "../UI/Button";
import Callout from "../UI/Callout";
import Text from "../UI/Text";
import CheckboxStandalone from "../UI/CheckboxStandalone";
// @ts-expect-error pure js
import { ReactSelectStandalone } from "../UI/SelectStandalone";
import DocumentDownloadLink from "../UI/DocumentDownloadLink";
// @ts-expect-error pure js
import DocumentThumbnail from "../UI/DocumentThumbnail";
// @ts-expect-error pure js
import { handleAPIError } from "../../utils/index.jsx";
import useCrg from "../../hooks/use-crg";
import type { CrgBulkSummary } from "../../models/crg";
import type { ProfileBailleur } from "./types";
import {
  buildCrgMonthOptions,
  currentCrgMonth,
  type CrgMonthOption,
} from "./crgMonths";
import styles from "./BulkCrgModal.module.css";

interface BulkCrgModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  profiles: ProfileBailleur[];
  /** Appelé une fois les CRG générés, par exemple pour vider la sélection. */
  onGenerated?: () => void;
}

export default function BulkCrgModal({
  open,
  onOpenChange,
  profiles,
  onGenerated,
}: BulkCrgModalProps) {
  const { genererCrgs } = useCrg();
  const monthOptions = useMemo(() => buildCrgMonthOptions(), []);
  const [selectedMonth, setSelectedMonth] = useState<CrgMonthOption | null>(
    null,
  );
  const [sendMail, setSendMail] = useState(false);
  const [summary, setSummary] = useState<CrgBulkSummary | null>(null);
  const [error, setError] = useState<ReactNode>("");
  const [isLoading, setIsLoading] = useState(false);

  const isCurrentMonth = selectedMonth?.value === currentCrgMonth();
  const profileNames = new Map(
    profiles.map((profile) => [profile.id, profile.nom_profile]),
  );

  function close() {
    setSelectedMonth(null);
    setSendMail(false);
    setSummary(null);
    setError("");
    onOpenChange(false);
  }

  async function generate() {
    if (!selectedMonth) return;
    setIsLoading(true);
    setError("");
    try {
      setSummary(
        await genererCrgs(
          profiles.map((profile) => profile.id),
          selectedMonth.value,
          sendMail,
        ),
      );
      onGenerated?.();
    } catch (err) {
      setError(handleAPIError(err));
    } finally {
      setIsLoading(false);
    }
  }

  const header =
    profiles.length > 1
      ? `Comptes rendus de gestion — ${profiles.length} bailleurs`
      : `Compte rendu de gestion — ${profiles[0]?.nom_profile ?? ""}`;

  return (
    <ModalDialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) close();
      }}
      header={header}
      size="md"
    >
      {summary ? (
        <>
          <ModalDialogBody className={styles.body}>
            <Callout variant="success" title="CRG générés">
              {summary.documents.length > 1
                ? `${summary.documents.length} comptes rendus de gestion ${
                    selectedMonth?.label ?? ""
                  } sont disponibles dans l'onglet Documents de chaque profil.`
                : `Le compte rendu de gestion ${
                    selectedMonth?.label ?? ""
                  } est disponible dans l'onglet Documents du profil.`}
            </Callout>
            <div className={styles.previews}>
              {summary.documents.map((document) => (
                <div key={document.id} className={styles.preview}>
                  <DocumentDownloadLink
                    documentId={document.id}
                    file={document}
                    className={styles.thumbnail}
                  >
                    <DocumentThumbnail
                      documentId={document.id}
                      file={document}
                    />
                  </DocumentDownloadLink>
                  <Text variant="deci">
                    {profileNames.get(document.id_profiles_nom) ?? ""}
                  </Text>
                </div>
              ))}
            </div>
            <Callout variant="success">
              {summary.sent.length > 0 &&
                `Envoyé par email à : ${summary.sent.join(", ")}.`}
            </Callout>
            <Callout variant="warning">
              {summary.skipped.length > 0 &&
                `Non traités : ${summary.skipped.join(" ; ")}.`}
            </Callout>
          </ModalDialogBody>
          <ModalDialogFooter>
            <Button onClick={close}>Fermer</Button>
          </ModalDialogFooter>
        </>
      ) : (
        <>
          <ModalDialogBody className={styles.body}>
            <Text>
              Génère le compte rendu de gestion du mois choisi pour chaque
              bailleur sélectionné. Chaque CRG est rangé dans les documents de
              son profil.
            </Text>
            {profiles.length > 1 && (
              <Text variant="deci">
                {profiles.map((profile) => profile.nom_profile).join(", ")}
              </Text>
            )}
            <ReactSelectStandalone
              label="Mois du compte rendu"
              options={monthOptions}
              value={selectedMonth}
              onChange={setSelectedMonth}
              placeholder="Sélectionner un mois"
              isClearable={false}
              menuPortalTarget={document.body}
            />
            <CheckboxStandalone
              checked={sendMail}
              onChange={(e) => setSendMail(e.target.checked)}
            >
              Envoyer chaque CRG par email au bailleur, à l'adresse renseignée
              sur son profil
            </CheckboxStandalone>
            <Callout variant="info">
              {isCurrentMonth &&
                "Le mois n'est pas terminé : les comptes rendus reprendront les loyers, règlements et dépenses enregistrés à ce jour. Les opérations saisies après la génération n'y figureront pas."}
            </Callout>
            <Callout variant="danger">{error}</Callout>
          </ModalDialogBody>
          <ModalDialogFooter>
            <Button variant="secondary" onClick={close}>
              Annuler
            </Button>
            <Button
              onClick={generate}
              disabled={!selectedMonth || profiles.length === 0}
              isLoading={isLoading}
            >
              {profiles.length > 1
                ? `Générer ${profiles.length} CRG`
                : "Générer le CRG"}
            </Button>
          </ModalDialogFooter>
        </>
      )}
    </ModalDialog>
  );
}
